import React, { useEffect, useState, useContext } from 'react';
import { useForm } from 'react-hook-form';
import './ManageClientInfo.redesign.scss';
import useFetch from 'use-http';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import { FormRow } from 'src/components/controls/forms/reactHookForm';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { AnyObject } from 'final-form';
import Client from 'src/models/Client';
import ClientSettingsFormData from 'src/models/ClientSettingsFormData';
import ClientSettings from 'src/models/ClientSettings';
import DialogList from 'src/dialogs';
import DocumentManagement from 'src/components/layout/DocumentManagement';
import State from 'src/models/State';
import _ from 'lodash';

import LoadingSpinner from 'src/components/common/LoadingSpinner';
import Address from 'src/models/Address';
import PhoneNumber from 'src/models/PhoneNumber';
import PagedResultSet from 'src/models/common/PagedResultSet';
import PhoneNumberType from 'src/models/PhoneNumberType';

interface IManageClientInfoProps {
    states: PagedResultSet<State>;
    showDialog: (dialogName: string) => void;
    hideDialog: () => void;
}

const ManageClientInfo = (props: IManageClientInfoProps) => {
    const { register, handleSubmit, errors, reset } = useForm();
    const { get, response, put, post } = useFetch();
    const appContext = useContext<IAppContextState>(AppContext);

    const [formData, setFormData] = useState(null);
    const [clientData, setClientData] = useState<Client>(null);
    const [clientSettingsData, setClientSettingsData] = useState<ClientSettings>(null);
    const [phoneTypes, setPhoneTypes] = useState<PhoneNumberType[]>([]);
    const [succesfulSubmit, setSuccesfulSubmit] = useState(false);

    const [useBillingAddress, setUseBillingAddress] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let mount = true;

        if (mount) {
            getClientData();
            getClientSettingsData();
            getPhoneTypes();
        }

        return () => {
            mount = false;
        };
    }, []);

    useEffect(() => {
        getClientData();
        getClientSettingsData();
    }, [succesfulSubmit]);

    useEffect(() => {
        prepFormData();
    }, [clientData, clientSettingsData]);

    useEffect(() => {
        reset(formData);
    }, [formData]);

    const getClientData = async () => {
        setIsLoading(true);
        const client: Client = await get(`v1.0/Clients/${appContext.clientNumber}`);

        if (response.ok) {
            setClientData(client);
            setIsLoading(false);
        } else {
            console.log('Unable to get Clients Data.');
        }
    };

    const getClientSettingsData = async () => {
        setIsLoading(true);
        const clientSettings: ClientSettings = await get(`v1.0/Clients/client-settings/${appContext.clientNumber}`);

        if (response.ok) {
            setClientSettingsData(clientSettings);
            setIsLoading(false);
        } else {
            console.log('Unable to get Clients Settings.', clientSettings);
        }
    };

    const getPhoneTypes = async () => {
        const data: PhoneNumberType[] = await get('v1.0/PhoneNumberTypes');

        if (response.ok) {
            setPhoneTypes(data);
        } else {
            console.log('Unable to get Phone Number Types', data);
        }
    };

    const prepFormData = () => {
        let prepClient: Partial<Client> = {
            id: clientData ? clientData?.id : null,
            name: clientData ? clientData?.name : '',
            address: clientData?.address,
            phoneNumber: clientData ? clientData?.phoneNumber : null,
            isShadow: clientData ? clientData?.isShadow : false,
            cloakName: clientData ? clientData?.cloakName : '',
            calmsSourced: clientData ? clientData.calmsSourced : false,
            clientNumber: appContext.clientNumber ? appContext.clientNumber : 0,
            webSite: clientData ? clientData?.webSite : '',
        };

        let prepClientSettings: Partial<ClientSettings> = {
            id: clientSettingsData ? clientSettingsData?.id : null,
            useBillingAddress: false,
            billingName: clientSettingsData ? clientSettingsData?.billingName : '',
            billingAddress: clientSettingsData?.billingAddress,
            billingPhone: clientSettingsData ? clientSettingsData?.billingPhone : undefined,
            billingCountry: clientSettingsData ? clientSettingsData?.billingCountry : '',
            clientType: clientSettingsData ? clientSettingsData?.clientType : '',
        };

        let formatedData: AnyObject = {
            client: prepClient,
            clientSettings: prepClientSettings,
        };

        setFormData(formatedData);
    };

    const filesUploded = () => {
        props.hideDialog();
    };

    const createClientSettingsRecord = async (data: ClientSettingsFormData) => {
        const result = await post(`v1.0/Clients/client-settings/${appContext.clientNumber}`, data);

        if (response.ok) {
            setSuccesfulSubmit(true);
        } else {
            console.log('Failed to create Client Settings Record', result);
        }
    };

    const updateClientRecord = async (data: ClientSettingsFormData) => {
        const result = await put(`v1.0/Clients/${appContext.clientNumber}`, data.client);

        if (response.ok) {
            console.log('Client record has been updated ', result);
            setSuccesfulSubmit(true);
        }
    };

    const updateClientSettingsRecord = async (data: ClientSettingsFormData) => {
        const result = await put(`v1.0/Clients/client-settings/${appContext.clientNumber}`, data.clientSettings);

        if (response.ok) {
            console.log('Client Settings have been updated ', result);
            setUseBillingAddress(true);
            setSuccesfulSubmit(true);
        }
    };

    const formatSubmitData = (values: ClientSettingsFormData) => {
        // "Sanitizing" Client Data
        let formatClientData: Client = {
            id: 0,
            name: '',
            address: new Address(),
            phoneNumber: new PhoneNumber(),
            isShadow: false,
            cloakName: '',
            calmsSourced: false,
            clientNumber: 0,
            webSite: '',
            isRealVisible: false,
        };
        formatClientData.id = clientData.id ? parseInt(clientData.id + '', 10) : null;
        formatClientData.clientNumber = appContext.clientNumber ? appContext.clientNumber : 0;
        formatClientData.isShadow = !!clientData.isShadow ? clientData.isShadow : false;
        formatClientData.name = values.client.name ? values.client.name : '';
        formatClientData.cloakName = values.client.name ? values.client.name : '';
        formatClientData.calmsSourced = !!clientData.calmsSourced ? clientData.calmsSourced : false;
        formatClientData.address.id = clientData?.address?.id ? clientData?.address?.id : undefined;
        formatClientData.address.addressLine1 = values.client.address.addressLine1 ? values.client.address.addressLine1 : '';
        formatClientData.address.addressLine2 = values.client.address.addressLine2 ? values.client.address.addressLine2 : '';
        formatClientData.address.zipCode = values.client.address.zipCode ? values.client.address.zipCode : '';
        formatClientData.address.city = values.client.address.city ? values.client.address.city : '';
        formatClientData.address.state.id = values.client.address.state.id ? parseInt(values.client.address.state.id + '', 10) : null;
        formatClientData.address.stateId = values.client.address.state.id ? parseInt(values.client.address.state.id + '', 10) : null;
        formatClientData.phoneNumber = clientData.phoneNumber ? clientData.phoneNumber : null;
        formatClientData.webSite = clientData.webSite ? clientData.webSite : '';
        formatClientData.isRealVisible = !!clientData.isRealVisible ? clientData.isRealVisible : false;

        // "Sanitizing" Client Settings Data
        let formatClientSettingsData: ClientSettings = {
            id: 0,
            useBillingAddress: false,
            billingName: '',
            billingAddress: new Address(),
            billingCountry: '',
            clientType: '',
        };
        formatClientSettingsData.id = clientSettingsData?.id ? parseInt(clientSettingsData.id + '', 10) : null;
        formatClientSettingsData.country = '';
        formatClientSettingsData.clientType = values.clientSettings.clientType ? values.clientSettings.clientType : '';
        formatClientSettingsData.useBillingAddress = !!values.clientSettings.useBillingAddress ? values.clientSettings.useBillingAddress : false;
        formatClientSettingsData.billingName = values.clientSettings.billingName ? values.clientSettings.billingName : '';
        if (values.clientSettings.billingPhone.number && values.clientSettings.billingPhone.number.length > 0) {
            formatClientSettingsData.billingPhone = new PhoneNumber();
            formatClientSettingsData.billingPhone.id = clientSettingsData?.billingPhone?.id
                ? parseInt(clientSettingsData.billingPhone.id + '', 10)
                : null;
            formatClientSettingsData.billingPhone.number = values.clientSettings.billingPhone.number ? values.clientSettings.billingPhone.number : '';
            formatClientSettingsData.billingPhone.typeId = values.clientSettings.billingPhone.typeId
                ? parseInt(values.clientSettings.billingPhone.typeId + '', 10)
                : 0;
        }

        formatClientSettingsData.billingCountry = values.clientSettings.billingCountry ? values.clientSettings.billingCountry : '';
        formatClientSettingsData.billingAddress.id = clientSettingsData?.billingAddress?.id
            ? parseInt(clientSettingsData.billingAddress.id + '', 10)
            : null;
        formatClientSettingsData.billingAddress.addressLine1 = values.clientSettings.billingAddress.addressLine1
            ? values.clientSettings.billingAddress.addressLine1
            : '';
        formatClientSettingsData.billingAddress.addressLine2 = values.clientSettings.billingAddress.addressLine2
            ? values.clientSettings.billingAddress.addressLine2
            : '';
        formatClientSettingsData.billingAddress.city = values.clientSettings.billingAddress.city ? values.clientSettings.billingAddress.city : '';
        formatClientSettingsData.billingAddress.zipCode = values.clientSettings.billingAddress.zipCode
            ? values.clientSettings.billingAddress.zipCode
            : '';
        formatClientSettingsData.billingAddress.state.id = values.clientSettings.billingAddress.state.id
            ? parseInt(values.clientSettings.billingAddress.state.id + '', 10)
            : 0;
        formatClientSettingsData.billingAddress.stateId = values.clientSettings.billingAddress.state.id
            ? parseInt(values.clientSettings.billingAddress.state.id + '', 10)
            : 0;

        let formatedData: AnyObject = {
            client: formatClientData,
            clientSettings: formatClientSettingsData,
        };
        return formatedData as ClientSettingsFormData;
    };

    const onClickHandler = (event: AnyObject) => {
        setUseBillingAddress(!event.target.checked);
    };

    // Options for the state dropdown selection
    const stateDropdownOptions = () => {
        return props.states
            ? props.states?.items?.map((st: State) => {
                  return { value: st.id, label: st.name };
              })
            : [];
    };

    const phoneTypeDropdownOptions = () => {
        return phoneTypes
            ? phoneTypes.map((pt: PhoneNumberType) => {
                  return { value: pt.id, label: pt.type };
              })
            : [];
    };

    const onSubmit = (values: ClientSettingsFormData) => {
        const newValues = formatSubmitData(values);

        if (_.isEqual(newValues.client, formData.client)) {
            console.log('No change in Client Data');
        } else {
            updateClientRecord(newValues);
        }

        if (!clientSettingsData.id) {
            createClientSettingsRecord(newValues);
        }

        if (_.isEqual(newValues.clientSettings, formData.clientSettings)) {
            console.log('No change in Client Settings Data');
        } else {
            updateClientSettingsRecord(newValues);
        }
    };

    return (
        <div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div style={{ display: 'flex', marginBottom: '3rem' }}>
                    <div className="section-size">
                        <RHFLegend title="Client Information" />
                        {isLoading ? (
                            <LoadingSpinner />
                        ) : (
                            <FormRow style={{ display: 'flex', flexWrap: 'wrap', marginTop: '2.75rem' }}>
                                <div className="input-size">
                                    <RHFInputBox
                                        name={'client.name'}
                                        label="Client Name"
                                        register={register}
                                        errors={errors}
                                        rules={{ required: 'This field is required' }}
                                        type="text"
                                    />
                                </div>
                                <div className="input-size">
                                    <RHFInputBox
                                        name={'client.address.addressLine1'}
                                        label="Address Line 1"
                                        register={register}
                                        errors={errors}
                                        rules={{ required: 'This field is required' }}
                                        type="text"
                                    />
                                </div>
                                <div className="input-size">
                                    <RHFInputBox
                                        name={'client.address.addressLine2'}
                                        label="Address Line 2"
                                        register={register}
                                        errors={errors}
                                        type="text"
                                    />
                                </div>
                                {/* <div className="input-size">
                                <RHFInputBox name="country" label="Country" register={register} errors={errors} type="text" />
                            </div> */}
                                <div className="input-size">
                                    <RHFInputBox
                                        name={'client.address.city'}
                                        label="City"
                                        register={register}
                                        errors={errors}
                                        rules={{ required: 'This field is required' }}
                                        type="text"
                                    />
                                </div>
                                <div className="input-size">
                                    <RHFSelect
                                        label="State"
                                        name="client.address.state.id"
                                        options={stateDropdownOptions()}
                                        register={register}
                                        errors={errors}
                                    />
                                </div>
                                <div className="input-size">
                                    <RHFInputBox
                                        name={'client.address.zipCode'}
                                        label="Zip Code"
                                        register={register}
                                        errors={errors}
                                        rules={{ required: 'This field is required' }}
                                        type="number"
                                    />
                                </div>
                            </FormRow>
                        )}
                    </div>
                    <div className="section-size">
                        <RHFLegend title="Client Billing Address" />
                        {isLoading ? (
                            <LoadingSpinner />
                        ) : (
                            <>
                                <div className="flex-gap" style={{ marginBottom: '1.5rem' }}>
                                    <input type="checkbox" name="clientSettings.useBillingAddress" ref={register} onClick={onClickHandler} />
                                    <label htmlFor="">Use Billing Address</label>
                                </div>
                                <FormRow style={{ display: 'flex', flexWrap: 'wrap' }}>
                                    <div className="input-size">
                                        <RHFInputBox
                                            name="clientSettings.billingName"
                                            label="Client Name"
                                            register={register}
                                            errors={errors}
                                            disabled={useBillingAddress}
                                        />
                                    </div>
                                    <div className="input-size">
                                        <RHFInputBox
                                            name="clientSettings.billingAddress.addressLine1"
                                            label="Address Line1"
                                            register={register}
                                            errors={errors}
                                            disabled={useBillingAddress}
                                            rules={useBillingAddress ? {} : { required: 'This field is required' }}
                                        />
                                    </div>
                                    <div className="input-size">
                                        <RHFInputBox
                                            name="clientSettings.billingAddress.addressLine2"
                                            label="Address Line2"
                                            register={register}
                                            errors={errors}
                                            disabled={useBillingAddress}
                                        />
                                    </div>
                                    {/* <div className="input-size">
                                <RHFInputBox name="country" label="Country" register={register} errors={errors} disabled={true} />
                            </div> */}
                                    <div className="input-size">
                                        <RHFInputBox
                                            name="clientSettings.billingAddress.zipCode"
                                            label="Zip Code"
                                            register={register}
                                            errors={errors}
                                            disabled={useBillingAddress}
                                            rules={useBillingAddress ? {} : { required: 'This field is required' }}
                                        />
                                    </div>
                                    <div className="input-size">
                                        <RHFInputBox
                                            name="clientSettings.billingAddress.city"
                                            label="City"
                                            register={register}
                                            errors={errors}
                                            disabled={useBillingAddress}
                                        />
                                    </div>
                                    <div className="input-size">
                                        <RHFSelect
                                            label="State"
                                            name="clientSettings.billingAddress.state.id"
                                            options={stateDropdownOptions()}
                                            register={register}
                                            errors={errors}
                                            disabled={useBillingAddress}
                                        />
                                    </div>
                                    <div className="input-size">
                                        <RHFInputBox
                                            name="clientSettings.billingPhone.number"
                                            label="Phone"
                                            register={register}
                                            errors={errors}
                                            disabled={useBillingAddress}
                                        />
                                    </div>
                                    <div className="input-size">
                                        <RHFSelect
                                            label={'Phone Type'}
                                            name="clientSettings.billingPhone.typeId"
                                            options={phoneTypeDropdownOptions()}
                                            register={register}
                                            errors={errors}
                                            disabled={useBillingAddress}
                                        />
                                    </div>
                                    <div className="input-size">
                                        <RHFSelect
                                            label={'Client Type'}
                                            name="clientSettings.clientType"
                                            options={[
                                                { value: 'land', label: 'Land' },
                                                { value: 'noLand', label: 'No-Land' },
                                            ]}
                                            disabled={useBillingAddress}
                                            register={register}
                                            errors={errors}
                                        />
                                    </div>
                                </FormRow>
                            </>
                        )}
                    </div>
                </div>
                <div style={{ marginTop: '1rem', borderTop: '1px solid grey' }}>
                    <Button
                        className="blue-hover-button"
                        text="Attach Documents"
                        intent="secondary"
                        onClick={() => props.showDialog(DialogList.DOCUMENT_MANAGEMENT)}
                        style={{ marginTop: '1rem' }}
                    />
                </div>

                <div style={{ marginTop: '4rem' }}>
                    <Button text="Submit" intent="primary" type="submit" />
                </div>
            </form>
            <DocumentManagement filesUploaded={filesUploded} context="client settings" />
        </div>
    );
};

export default ManageClientInfo;



// WEBPACK FOOTER //
// ./src/components/project/sections/ManageClientInfo.redesign.tsx